import React, { useMemo, useState } from "react";
import { ClickAwayListener, Popper } from "@mui/material";
import { Link } from "react-router-dom";
import { useTooltipExitWithSnapshot } from "../hooks/useTooltipExitWithSnapshot";
import { TooltipPlacement } from "../libs/favoriteMessageTooltip";

type Props = {
    open: boolean;
    anchorEl: HTMLElement | null;
    message: string;
    to?: string;
    placement?: TooltipPlacement;
    autoHideMs?: number;
    onClose: () => void;
};

export const FavoriteMessageTooltip: React.FC<Props> = ({
    open,
    anchorEl,
    message,
    to,
    placement = "top",
    autoHideMs = 5000,
    onClose,
}) => {
    const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(
        null
    );
    const {
        shouldRender,
        isExitAnimating,
        tooltipSnapshot,
        handleAnimationEnd,
    } = useTooltipExitWithSnapshot({
        open,
        anchorEl,
        message,
        to,
        placement,
        autoHideMs,
        onClose,
    });
    const {
        anchorEl: tooltipAnchorEl,
        message: tooltipMessage,
        to: tooltipTo,
        placement: tooltipPlacement,
    } = tooltipSnapshot;
    const isTopPlacement = tooltipPlacement === "top";
    const modifiers = useMemo(
        () => [
            {
                name: "offset",
                options: {
                    offset: [0, 6],
                },
            },
            {
                name: "arrow",
                options: {
                    element: arrowElement,
                    padding: 6,
                },
            },
        ],
        [arrowElement]
    );

    if (!shouldRender || !tooltipAnchorEl) return null;

    return (
        <Popper
            open
            anchorEl={tooltipAnchorEl}
            placement={tooltipPlacement}
            modifiers={modifiers}
            sx={{ zIndex: 30 }}
        >
            <ClickAwayListener onClickAway={onClose}>
                <div
                    className={`relative ${
                        isExitAnimating
                            ? "animate-favoriteTooltipExit"
                            : "animate-favoriteTooltipEnter"
                    }`}
                    onAnimationEnd={handleAnimationEnd}
                >
                    {tooltipTo ? (
                        <Link
                            to={tooltipTo}
                            className="block whitespace-nowrap rounded-lg bg-primary px-3 py-2 text-sm text-white shadow-md hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                            onClick={onClose}
                        >
                            {tooltipMessage}
                        </Link>
                    ) : (
                        <div className="block whitespace-nowrap rounded-lg bg-primary px-3 py-2 text-sm text-white shadow-md">
                            {tooltipMessage}
                        </div>
                    )}
                    <div
                        ref={setArrowElement}
                        data-popper-arrow
                        className={`absolute h-0 w-0 border-x-8 border-x-transparent ${
                            isTopPlacement
                                ? "-bottom-2 border-t-8 border-t-primary"
                                : "-top-2 border-b-8 border-b-primary"
                        }`}
                    />
                </div>
            </ClickAwayListener>
        </Popper>
    );
};
